import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { ConfirmationService } from 'src/app/modules/shared/services/confirmation.service';
import { RisksService } from '../../services/risks.service';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { RiskRAGMatrixService } from 'src/app/modules/shared/services/risk-rag-matrix.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { AccountSettingsService } from 'src/app/modules/shared/services/account-settings.service';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { SuggestedRiskViewModel } from '../../models/SuggestedRiskViewModel';
import { ButtonSizes } from 'src/app/modules/shared/enums/styles/buttonSizes.enum';
import { ButtonThemes } from 'src/app/modules/shared/enums/styles/buttonThemes.enum';
import { EditableFieldTypes } from 'src/app/modules/shared/enums/editableFieldTypes';
import { Employee } from 'src/app/modules/shared/models/employee';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { T } from 'src/assets/i18n/translation-keys';
import { Subscription } from 'rxjs';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { RiskImpact } from '../../enums/riskImpact';
import { RiskLikelihoods } from 'src/app/modules/shared/enums/risks/riskLikelihoods';
import { RiskDetailsViewModel } from '../../models/RiskDetailsViewModel';
import { TemplateRiskViewModel } from '../../models/templateRiskViewModel';
import { RiskRagHelper } from 'src/app/modules/shared/utilities/risk-rag.utilities copy';
import { Constants } from 'src/app/modules/shared/models/constants';

@Component({
    template: '',
    standalone: false
})
export class RiskAddModalCoreComponent implements OnInit, OnDestroy {
  public risk: TemplateRiskViewModel | RiskDetailsViewModel;
  public filters: FilterViewModel[] = [];
  protected allowedFilters: FilterViewModel[];
  public form: FormGroup;
  public selectedSuggestedRisk: SuggestedRiskViewModel;
  public usedPreDefinedRisk: boolean = false;

  public readonly T = T;
  public isLoading = false;
  protected readonly ButtonSizes = ButtonSizes;
  protected readonly ButtonThemes = ButtonThemes;
  public editableFieldTypes = EditableFieldTypes;
  public employee: Employee;
  public objectTypes = ObjectTypes;
  protected isTouched = false;
  protected subscriptions = new Subscription();
  protected mobileMaxWidth = Constants.sm;
  public isMobile: boolean = window.innerWidth < this.mobileMaxWidth;

  public triggerErrors = false;
  public filterTypes = FilterTypes;
  public likelihoodValue = RiskLikelihoods.Highly_Unlikely;
  public impactValue = RiskImpact.Negligible.valueOf();
  public statusValue = 0;
  public privacyValue = 0;
  public riskStrategyValue = 0;
  public ownerValue = null;
  public riskIdentifiedByValue = 0;
  public localisedImpact = 'Impact';
  public localisedDepartment: string = 'Department';
  public templateRisk: string;

  public ragDescription: string;
  public ragColour: string;
  public ragBackgroundColour: string;


  constructor(
    protected bsModalRef: BsModalRef,
    protected router: Router,
    protected fb: UntypedFormBuilder,
    protected changeDetectorRef: ChangeDetectorRef,
    protected confirmationService: ConfirmationService,
    protected allowedFiltersService: AllowedFiltersService,
    protected authenticationService: AuthenticationService,
    protected riskService: RisksService,
    protected readonly localisationService: LocalisationService,
    protected readonly riskRAGMatrixService: RiskRAGMatrixService,
    protected readonly alertService: AlertService,
    protected readonly translateService: TranslateService,
    protected readonly wtValidators: WtValidators,
    protected readonly accountSettingsService: AccountSettingsService
  ) {}

  ngOnInit(): void {
    this.employee = this.authenticationService.getCurrentEmployee();
    this.localisedImpact = this.localisationService.localiseFilterType(FilterTypes.Risk_Impact);
    this.localisedDepartment = this.localisationService.localiseObjectType(ObjectTypes.Department);

    this.isMobile = window.innerWidth < this.mobileMaxWidth;

    this.initNewForm();

    this.subscriptions.add(
      this.form.valueChanges.subscribe((res) => {
        this.isTouched = true;
      })
    );

    this.initAllowedFilters();
    this.setRAGInformation();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public initAllowedFilters(): void {
    //override
  }

  initNewForm() {
    this.form = this.fb.group({
      riskTitle: [
        '',
        {
          validators: [
            Validators.required,
            Validators.maxLength(250),
            this.wtValidators.textInput(),
            this.wtValidators.restrictedChars([';'])
          ],
          updateOn: 'blur'
        }
      ],
      // Fix description after a merge with latest Dev branch
      riskDescription: ['', Validators.maxLength(2000)],
      owner: [null],
    });
  }

  public handleFilterChange(filters: FilterViewModel[]): void {
    this.filters = filters;

    this.impactValue = filters.find((f) => f.filterType === FilterTypes.Risk_Impact)?.filterValue;
    this.statusValue = filters.find((f) => f.filterType === FilterTypes.Risk_Status)?.filterValue;
    this.riskStrategyValue = filters.find((f) => f.filterType === FilterTypes.Risk_Strategy)?.filterValue;
    this.likelihoodValue = filters.find((f) => f.filterType === FilterTypes.Risk_Likelihood)?.filterValue;
    this.privacyValue = filters.find((f) => f.filterType === FilterTypes.Risk_Status)?.filterValue;
    this.ownerValue = filters.find((f) => f.filterType === FilterTypes.Owner)?.filterValue;
    this.riskIdentifiedByValue = filters.find((f) => f.filterType === FilterTypes.Identified_By)?.filterValue;

    if (filters.length > 4) {
      this.isTouched = true;
    }

    this.changeDetectorRef.detectChanges();
    this.setRAGInformation();
  }

  public closeModal(): void {
    this.bsModalRef.hide();
  }

  public confirmClose(): void {
    if (this.isTouched) {
      this.confirmationService.confirmThis(
        this.translateService.instant(T.common.are_sure_you_want_to_leave_page),
        () => {
          this.closeModal();
        },
        () => {
          //handle cancel
        },
        this.translateService.instant(T.common.confirm),
        false,
        this.translateService.instant(T.common.stay),
        this.translateService.instant(T.common.leave),
        'danger'
      );
    } else {
      this.closeModal();
    }
  }

  public validateState() {
    // override
  }

  public updateRiskDescription($event: string): void {
    this.form.controls.riskDescription.setValue($event);
  }

  public setMyDepartment(): void {
    if (this.allowedFilters) {
      const empFilter = this.allowedFilters.find(
        (f) => f.filterType === FilterTypes.Department && f.filterValue.toString() === this.employee.departmentId.toString()
      );
      if (!this.filters.some((x) => x.id == empFilter.id)) {
        if (empFilter && this.filters.indexOf(empFilter) < 0) {
          this.filters = this.filters.slice();
          this.filters.push(empFilter);
        }
      }
    }
    this.changeDetectorRef.detectChanges();
  }

   public setAccountForCurrentOwner(): void {
    if (this.allowedFilters) {
      const empFilter = this.allowedFilters.find(
        (s: any) => s.filterType === FilterTypes.Owner && s.filterValue.toString() === this.employee.id.toString()
      );
      if (empFilter !== null) {
        if (this.filters.indexOf(empFilter) < 0) {
          this.filters = this.filters.slice();
          this.filters.push(empFilter);
        }
      }
    }
    this.ownerValue = this.filters.find((f) => f.filterType === FilterTypes.Owner).filterValue;

    this.changeDetectorRef.markForCheck();
  }

  protected getFilterValue(filterType: FilterTypes): number {
    const filter: FilterViewModel = this.risk?.filters?.find((f) => f.filterType === filterType);
    return filter ? filter.filterValue : 0;
  }

  protected setRAGInformation(): void {
    this.risk.rag = this.riskRAGMatrixService.getRAGFromImpactAndLikelihood(this.impactValue, this.likelihoodValue);
    this.ragDescription = this.localisationService.localiseFilterValueByFilterType(this.risk.rag, this.filterTypes.Risk_RAG);
    this.ragColour = RiskRagHelper.getRAGColourHexFromValue(this.risk.rag);
    this.ragBackgroundColour = RiskRagHelper.getRAGBackgroundColourHexFromValue(this.risk.rag);

    this.changeDetectorRef.markForCheck();
  }

  protected handlePrefillForm(eventData: { item: any, notEditable: boolean }): void{
    if (eventData.item?.id === this.selectedSuggestedRisk?.id) {
      this.selectedSuggestedRisk = null;
      this.form.patchValue({
        riskTitle: '',
        riskDescription: ''
      });

      this.impactValue = RiskImpact.Negligible.valueOf();
      this.likelihoodValue = RiskLikelihoods.Highly_Unlikely;
      this.usedPreDefinedRisk = false;
    } else {
      this.selectedSuggestedRisk = eventData.item;

      this.form.patchValue({
        riskTitle: eventData.item.title,
        riskDescription: eventData.item.description,
      });

      this.impactValue = eventData.item.impact
      this.likelihoodValue = eventData.item.likelihood
      this.usedPreDefinedRisk = eventData.notEditable;
    }

    this.setRAGInformation();
    this.changeDetectorRef.detectChanges();
  }
}
